// React context for the current section's background tone. A `section` block
// sets this so descendant blocks that need to swap MARKUP (not just CSS color)
// based on dark vs light backgrounds can read it without prop-drilling.
//
// Most adaptation is pure CSS via the `[data-bg]` attribute the section block
// puts on its wrapper (see sections.css). This context exists for the few cases
// that need it in JS — e.g. choosing a white-on-dark logo variant.
//
// Default is 'light': content sections are light-canvas by default, and any
// block rendered outside a section (legacy flat-block pages) keeps its existing
// behavior because the legacy CSS is unscoped and unaffected by this attribute.

import { createContext, useContext, type ReactNode } from 'react';

export type BgTone = 'dark' | 'light';

const BgCtx = createContext<BgTone>('light');

export function BgProvider({ tone, children }: { tone: BgTone; children: ReactNode }) {
  return <BgCtx.Provider value={tone}>{children}</BgCtx.Provider>;
}

/** Read the current section's background tone. 'light' when not inside a
 *  section block. Blocks use this only when their MARKUP differs by tone;
 *  color adaptation is handled in CSS via the section's `data-bg` attribute. */
export function useBg(): BgTone {
  return useContext(BgCtx);
}

/** Backgrounds a section block can declare. Each maps to a `.sc-bg-*` class
 *  and an implied tone (dark backgrounds get data-bg="dark"). */
export type SectionBackground = 'dark' | 'navy' | 'cream' | 'white' | 'warm' | 'coral';

export const DARK_BACKGROUNDS: ReadonlySet<SectionBackground> = new Set(['dark', 'navy', 'coral']);

export function toneFor(bg: SectionBackground): BgTone {
  return DARK_BACKGROUNDS.has(bg) ? 'dark' : 'light';
}
